import * as React from 'react';
import styled from 'styled-components';
import {SystemIcons} from '../icons';
import {COLORS, ComponentTextStyle, focusStyles} from '../styles';
import {Size, Testable} from '../types';
import {ComponentLStyling, ComponentMStyling, ComponentSStyling, ComponentXXSStyling} from '../styles';
import {defaultOnMouseDownHandler} from '../common';

const StyledRadioButton = styled.div<{ margin?: string, disabled?: boolean, invalid?: boolean, selected?: boolean }>`
  display: flex;
  width: 100%;
  min-height: 48px;
  min-width: 48px;

  ${props => (props.margin ? `margin: ${props.margin};` : '')}
  cursor: pointer;

  color: ${COLORS.black};

  .pointerTransparent {
    pointer-events: none;
  }

  .radio-button-icon {
    margin: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    height: 36px;
    min-width: 36px;
    border-radius: 50%;

    svg {
      color: ${props => (props.selected ? COLORS.primary_500 : props.invalid ? COLORS.critical_400 : COLORS.neutral_600)};
    }
  }

  .radio-button-label {
    user-select: none;
    display: flex;
    flex-direction: column;

    label {
      cursor: inherit;
    }

    span {
      ${ComponentXXSStyling(ComponentTextStyle.Regular, COLORS.black)}
    }
  }

  &.small {
    ${ComponentSStyling(ComponentTextStyle.Regular, COLORS.black)}
    .radio-button-label {
      padding-top: 14px;
    }
  }

  &.medium {
    ${ComponentMStyling(ComponentTextStyle.Regular, COLORS.black)}
    .radio-button-label {
      padding-top: 12px;
    }
  }

  &.large {
    ${ComponentLStyling(ComponentTextStyle.Regular, COLORS.black)}
    .radio-button-label {
      padding-top: 12px;
    }
  }

  &:not(.disabled):focus {
    ${focusStyles}
  }

  &:not(.disabled):hover {
    .radio-button-icon {
      background-color: ${COLORS.primary_20};

      svg {
        color: ${COLORS.primary_700};
      }
    }
  }

  &:not(.disabled):active {
    .radio-button-icon {
      background: ${COLORS.primary_100};

      svg {
        color: ${COLORS.primary_800};
      }
    }
  }

  &.disabled {
    cursor: not-allowed;

    color: ${COLORS.neutral_300};

    .radio-button-icon,
    .radio-button-label {
      pointer-events: none;
    }

    .radio-button-icon {
      background-color: ${COLORS.white};
    }

    .radio-button-icon svg {
      color: ${COLORS.neutral_300};
    }
  }

  &.dropdown-hover:not(.disabled) {
    .radio-button-icon {
      background-color: ${COLORS.primary_20};

      svg {
        color: ${COLORS.primary_700};
      }
    }
  }
`;

export type RadioButtonProps = Testable & Omit<React.HTMLAttributes<HTMLDivElement>, 'tabIndex' | 'className' | 'onClick' | 'onMouseDown' | 'onKeyDown'> & {
  id?: string;
  selected: boolean;
  select: (selected: boolean) => void;
  label?: string;
  additionalLabel?: string;
  invalid?: boolean;
  disabled?: boolean;
  iconPointerEventsTransparent?: boolean;
  margin?: string;
  size?: Size;
  tabIndexVal?: number;
  className?: string;
}

const RadioButton = React.forwardRef<HTMLDivElement, RadioButtonProps>(({
                                                               id,
                                                               selected,
                                                               label,
                                                               additionalLabel,
                                                               invalid,
                                                               select,
                                                               disabled,
                                                               margin,
                                                               iconPointerEventsTransparent,
                                                               size,
                                                               tabIndexVal,
                                                               className,
                                                               dataTestId,
                                                               ...rest
                                                             }: RadioButtonProps, ref) => {
  const onKeyPress = (e: any) => {
    if (e.keyCode === 13 && !disabled) {
      select(!selected);
    }
  };

  size = size ?? Size.Medium;

  const cls = `${size} ${className || ''} ${disabled ? ' disabled' : ''}`;

  return (
    <StyledRadioButton key={id}
                       margin={margin}
                       ref={ref}
                       onClick={() => !disabled && select(!selected)}
                       onMouseDown={defaultOnMouseDownHandler}
                       onKeyDown={onKeyPress}
                       disabled={disabled}
                       className={cls}
                       tabIndex={disabled ? -1 : (tabIndexVal ? tabIndexVal : 0)}
                       invalid={invalid}
                       selected={selected}
                       {...rest}>
      <div className={'radio-button-icon'} id={id} data-testid={dataTestId}>
        {selected && <SystemIcons.RadioButtonOn className={iconPointerEventsTransparent ? 'pointerTransparent' : ''} size="24px"/>}
        {!selected &&
          <SystemIcons.RadioButtonOff className={iconPointerEventsTransparent ? 'pointerTransparent' : ''} size="24px"/>}
      </div>
      <div className={'radio-button-label'}>
        {label && <label htmlFor={id}>{label}</label>}
        {additionalLabel !== undefined && <span>{additionalLabel}</span>}
      </div>
    </StyledRadioButton>
  );
});

export default RadioButton;
